import SideModal from "components/SideModal";
import { useTranslation } from "contexts/i18nContext";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useRef, useState } from "react";
import { addComplaintWitness } from "services/prosecutorDecipilineService";
import { CollectedWitness, Complaints } from "types";
import { LoadingIcon } from "utils/constant";

interface WitnessModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  complaintData: Complaints;
  onRefresh: any;
}

export default function AddWitnessModal({
  isOpen,
  setIsOpen,
  complaintData,
  onRefresh,
}: WitnessModalProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<CollectedWitness>({
    name: "",
    email: "",
    phone_number: "",
    address: "",
    witness_region: "",
    witness_zone: "",
    witness_city: "",
    witness_woreda: "",
    witness_kebele: "",
    id_numner: "",
    files: [] as File[],
    complaint: null,
  });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (fileList && fileList.length > 0) {
      const filesArray = Array.from(fileList) as File[];
      setFormData((prev) => ({
        ...prev,
        files: filesArray,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const compliantId = "uu_id" in complaintData ? complaintData?.uu_id : null;
    const isFormData = true;

    const data = new FormData();
    data.append("name", formData.name);
    data.append("phone_number", formData.phone_number);
    data.append("email", formData.email);
    data.append("address", formData.address);
    data.append("witness_region", formData.witness_region);
    data.append("witness_zone", formData.witness_zone);
    data.append("witness_city", formData.witness_city);
    data.append("witness_woreda", formData.witness_woreda);
    data.append("witness_kebele", formData.witness_kebele);
    data.append("id_numner", formData.id_numner);
    data.append("complaint", compliantId ? String(compliantId) : "");
    data.append("id_card_file", formData?.files[0] || "");

    try {
      await addComplaintWitness(data, isFormData);

      onRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");
      setFormData({
        name: "",
        email: "",
        phone_number: "",
        address: "",
        complaint: null,
        witness_region: "",
        witness_zone: "",
        witness_city: "",
        witness_woreda: "",
        witness_kebele: "",
        id_numner: "",
        files: [] as File[],
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <SideModal isOpen={isOpen} setIsOpen={setIsOpen} title={t("addWitness")}>
      <div className="px-1">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                {t("fullName")} <span className="text-red-600">*</span>
              </label>
              <input
                required
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-gray-800 focus:outline-none focus:ring-1 focus:ring-red-800"
                type="text"
                name="name"
                placeholder={t("fullNamePlaceholder")}
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  {t("phone")}
                </label>
                <input
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-gray-800 focus:outline-none focus:ring-1 focus:ring-red-800"
                  type="tel"
                  name="phone_number"
                  placeholder={t("phonePlaceholder")}
                  value={formData.phone_number}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  {t("email")}
                </label>
                <input
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-gray-800 focus:outline-none focus:ring-1 focus:ring-red-800"
                  type="email"
                  name="email"
                  placeholder={t("emailPlaceholder")}
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                {t("address")}
              </label>
              <input
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-gray-800 focus:outline-none focus:ring-1 focus:ring-red-800"
                type="text"
                name="address"
                placeholder={t("addressPlaceholder")}
                value={formData.address}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Location Information */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
              {t("locationDetails")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  {t("region")}
                </label>
                <input
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-gray-800 focus:outline-none focus:ring-1 focus:ring-red-800"
                  type="text"
                  name="witness_region"
                  placeholder={t("region")}
                  value={formData.witness_region}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  {t("zone")}
                </label>
                <input
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-gray-800 focus:outline-none focus:ring-1 focus:ring-red-800"
                  type="text"
                  name="witness_zone"
                  placeholder={t("zone")}
                  value={formData.witness_zone}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  {t("city")}
                </label>
                <input
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-gray-800 focus:outline-none focus:ring-1 focus:ring-red-800"
                  type="text"
                  name="witness_city"
                  placeholder={t("city")}
                  value={formData.witness_city}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  {t("woreda")}
                </label>
                <input
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-gray-800 focus:outline-none focus:ring-1 focus:ring-red-800"
                  type="text"
                  name="witness_woreda"
                  placeholder={t("woreda")}
                  value={formData.witness_woreda}
                  onChange={handleChange}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  {t("kebele")}
                </label>
                <input
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-gray-800 focus:outline-none focus:ring-1 focus:ring-red-800"
                  type="text"
                  name="witness_kebele"
                  placeholder={t("kebele")}
                  value={formData.witness_kebele}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* ID Information */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                {t("idNumber")}
              </label>
              <input
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-gray-800 focus:outline-none focus:ring-1 focus:ring-red-800"
                type="text"
                name="id_numner"
                placeholder={t("idNumber")}
                value={formData.id_numner}
                onChange={handleChange}
              />
            </div>

            {/* ID Card Upload */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                {t("idCard")} ({t("optional")})
              </label>
              <div className="border border-gray-300 rounded p-4">
                <input
                  type="file"
                  ref={fileInputRef}
                  name="files"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  accept=".pdf,.jpg,.jpeg,.png"
                />

                <div className="text-center">
                  <button
                    type="button"
                    onClick={triggerFileInput}
                    className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 transition-colors"
                  >
                    {t("uploadIdCard")}
                  </button>
                  <p className="text-xs text-gray-500 mt-2">
                    {t("fileTypesSimple")}
                  </p>
                </div>

                {/* File List */}
                {formData.files.length > 0 && (
                  <div className="mt-4">
                    {formData.files.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-red-100 rounded flex items-center justify-center">
                            <span className="text-red-600 text-xs font-bold">
                              {file.type.includes("pdf")
                                ? "PDF"
                                : file.type.includes("image")
                                  ? "IMG"
                                  : "FILE"}
                            </span>
                          </div>
                          <span className="text-sm text-gray-700 truncate">
                            {file.name}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              files: [],
                            }))
                          }
                          className="text-gray-500 hover:text-red-600"
                          aria-label={t("removeFile")}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
            >
              {t("cancel")}
            </button>

            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-red-800 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {t("submitting")}
                </>
              ) : (
                t("addWitness")
              )}
            </button>
          </div>
        </form>
      </div>
    </SideModal>
  );
}
